import { Avatar } from 'antd'
import { MessageOutlined } from '@ant-design/icons'
import React, { ReactNode, useEffect, useRef, useMemo } from 'react'
import dayjs from 'dayjs'
import localizedFormat from 'dayjs/plugin/localizedFormat'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import 'dayjs/locale/ru'
import {
  MessageContainer,
  MessageWrapper,
  MessageBubble,
  MessageContent,
  MessageText,
  MessageTime,
  AvatarWrapper,
  SenderName,
  DateDivider,
  EmptyState,
  MessageImage,
} from './styles'
import { IAvitoChat, IChat } from '../../model/types/types'
import { IMetadata } from 'src/react-app/pages/card-hr/new/ui/hr-chats/types'

dayjs.extend(localizedFormat)
dayjs.extend(isSameOrBefore)
dayjs.locale('ru')

interface IDataChat {
  data: IAvitoChat
  managerTitle?: string
  managerAvatar?: ReactNode
  userTitle?: string
  userAvatar?: ReactNode
  className?: string
  metadata?: IMetadata
}

interface MessageGroup {
  date: string
  messages: IChat[]
}

export const MessageBox = (props: IDataChat) => {
  const { metadata } = props
  const {
    data,
    className,
    managerTitle = 'Менеджер',
    managerAvatar = 'M',
    userTitle = metadata?.customerName || 'Кандидат',
    userAvatar = metadata?.customerName ? metadata.customerName[0] : 'К',
  } = props

  const messageContainerRef = useRef<HTMLDivElement>(null)
  const groupedMessages = useMemo(() => {
    if (!data?.chat?.length) return []

    const groups: MessageGroup[] = []
    let currentDate = ''

    data.chat.forEach(message => {
      const messageDate = dayjs(message.created_at).format('YYYY-MM-DD')

      if (messageDate !== currentDate) {
        currentDate = messageDate
        groups.push({
          date: messageDate,
          messages: [message],
        })
      } else {
        groups[groups.length - 1].messages.push(message)
      }
    })

    return groups
  }, [data?.chat])

  const formatDateDivider = (date: string) => {
    const messageDate = dayjs(date)
    const today = dayjs()
    const yesterday = dayjs().subtract(1, 'day')

    if (messageDate.isSame(today, 'day')) {
      return 'Сегодня'
    } else if (messageDate.isSame(yesterday, 'day')) {
      return 'Вчера'
    } else {
      return messageDate.format('D MMMM YYYY')
    }
  }

  const formatMessageTime = (date: string) => {
    return dayjs(date).format('HH:mm')
  }

  useEffect(() => {
    if (messageContainerRef.current) {
      messageContainerRef.current.scrollTo({
        top: messageContainerRef.current.scrollHeight,
        behavior: 'smooth',
      })
    }
  }, [data?.chat?.length])

  if (!data) {
    return null
  }

  if (!data?.chat?.length) {
    return (
      <MessageContainer className={className} ref={messageContainerRef}>
        <EmptyState>
          <MessageOutlined />
          <div>Пока нет сообщений</div>
          <div style={{ fontSize: 12, marginTop: 4 }}>Начните диалог с клиентом</div>
        </EmptyState>
      </MessageContainer>
    )
  }

  return (
    <MessageContainer className={className} ref={messageContainerRef}>
      {groupedMessages.map(group => (
        <div key={group.date}>
          <DateDivider>
            <span>{formatDateDivider(group.date)}</span>
          </DateDivider>

          {group.messages.map((message, messageIndex) => {
            const isOutgoing = message.direction === 'out'
            const avatar = isOutgoing ? managerAvatar : userAvatar
            const senderName = isOutgoing ? managerTitle : userTitle
            const isImage = message.type === 'image'

            return (
              <MessageWrapper
                key={`${message.chat_id}-${message.created_at}-${messageIndex}`}
                isOutgoing={isOutgoing}
              >
                <AvatarWrapper>
                  <Avatar
                    size={36}
                    style={{
                      backgroundColor: isOutgoing ? '#1890ff' : '#52c41a',
                      flexShrink: 0,
                    }}
                  >
                    {avatar}
                  </Avatar>
                </AvatarWrapper>

                <MessageBubble isOutgoing={isOutgoing}>
                  <MessageContent>
                    <SenderName isOutgoing={isOutgoing}>{senderName}</SenderName>

                    {isImage && message.metadata?.mediaUrl ? (
                      <>
                        <MessageImage
                          src={message.metadata.mediaUrl}
                          alt="Изображение"
                          onClick={() => window.open(message.metadata?.mediaUrl || '', '_blank')}
                        />
                        {message.msg && (
                          <MessageText className="message-text">{message.msg}</MessageText>
                        )}
                      </>
                    ) : (
                      <MessageText className="message-text">{message.msg}</MessageText>
                    )}

                    <MessageTime className="message-time">
                      {formatMessageTime(message.created_at)}
                    </MessageTime>
                  </MessageContent>
                </MessageBubble>
              </MessageWrapper>
            )
          })}
        </div>
      ))}
    </MessageContainer>
  )
}
